import React from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import {
    ChevronRight,
    Plus,
    BrainCircuit,
    Droplet,
    Thermometer,
    ArrowUp,
    ArrowDown,
    CloudRain,
    Droplets,
    LoaderCircle,
    CheckCircle2,
    Circle,
    AlertTriangle,
    Zap
} from 'lucide-react';
import '../styles/Prediction.css'; // Import the CSS file

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const PIPELINE_TEMPLATE = [
    {
        id: 'model-init',
        title: 'Model Initialized',
        description: 'Loading trained model parameters and scaler artifacts.'
    },
    {
        id: 'pipeline-ready',
        title: 'Pipeline Running',
        description: 'Validating latest input features and preparing inference batch.'
    },
    {
        id: 'inference',
        title: 'Inference Executed',
        description: 'Executing chlorine demand forecast through prediction endpoint.'
    },
    {
        id: 'post-process',
        title: 'Results Compiled',
        description: 'Aggregating outputs, confidence values, and date-wise demand.'
    }
];

const createInitialStages = () =>
    PIPELINE_TEMPLATE.map((stage) => ({
        ...stage,
        status: 'idle',
        note: 'Waiting to start'
    }));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const toMonthlyChartData = (consumptionData = []) =>
    consumptionData.map((item) => ({
        ...item,
        monthly_chlorine_consumption_kg: item.monthly_chlorine_consumption_l
    }));

const buildMonthlyConsumptionFromRows = (rows, year) => {
    if (!Array.isArray(rows)) return [];

    const monthlyTotals = new Map();

    rows.forEach((row) => {
        const rawDate = row?.date?.value ?? row?.date ?? row?.Date;
        if (!rawDate) return;

        const parsedDate = new Date(rawDate);
        if (Number.isNaN(parsedDate.getTime())) return;

        if (parsedDate.getUTCFullYear() !== year) return;

        const month = parsedDate.getUTCMonth() + 1;
        const pre = Number(row?.chlorine_pre_l_m ?? 0);
        const post = Number(row?.chlorine_post_l_m ?? 0);
        const dailyTotal = (Number.isFinite(pre) ? pre : 0) + (Number.isFinite(post) ? post : 0);
        const current = monthlyTotals.get(month) || 0;
        monthlyTotals.set(month, current + (dailyTotal * 24));
    });

    return Array.from(monthlyTotals.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([month, total]) => ({
            year,
            month,
            month_label: `${year}-${String(month).padStart(2, '0')}`,
            monthly_chlorine_consumption_l: Number(total.toFixed(2))
        }));
};

export default function Prediction() {
    // --- API State ---
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [predictions, setPredictions] = React.useState([]);
    const [pipelineStages, setPipelineStages] = React.useState(createInitialStages);
    const [lastRunMeta, setLastRunMeta] = React.useState(null);
    const [initialLoading, setInitialLoading] = React.useState(true);

    // Historical consumption state
    const [historicalData, setHistoricalData] = React.useState([]);
    const [selectedYear, setSelectedYear] = React.useState(2024);
    const [historicalLoading, setHistoricalLoading] = React.useState(false);
    const [historicalError, setHistoricalError] = React.useState(null);

    // Fetch latest forecast data only on mount
    React.useEffect(() => {
        fetchLatestPredictions();
    }, []);

    // Fetch historical data when year changes
    React.useEffect(() => {
        fetchHistoricalConsumption(selectedYear);
    }, [selectedYear]);

    const fetchLatestPredictions = async () => {
        try {
            setInitialLoading(true);
            setError(null);

            const backendUri = process.env.REACT_APP_BACKEND_URI || process.env.BACKEND_URI || '';
            const url = backendUri ? `${backendUri}/api/predict` : '/api/predict';

            const res = await fetch(url);
            if (!res.ok) {
                throw new Error(`Failed to fetch predictions (${res.status})`);
            }

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('Invalid response format from prediction service');
            }

            const data = await res.json();
            const latestPredictions = data.predictions || [];

            if (latestPredictions.length > 0) {
                setPredictions(latestPredictions);
                setLastRunMeta({
                    startedAt: new Date(), // We don't have the actual start time from backend
                    completedAt: new Date(),
                    rowCount: latestPredictions.length,
                    fetchedFromBackend: true
                });
            }
        } catch (err) {
            console.warn('Failed to fetch latest predictions:', err.message);
            // Don't set error state for initial load - just leave empty state
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchHistoricalConsumption = async (year) => {
        try {
            setHistoricalLoading(true);
            setHistoricalError(null);

            const backendUri = process.env.REACT_APP_BACKEND_URI || process.env.BACKEND_URI || '';
            const url = backendUri ? `${backendUri}/api/predictions/chlorine-consumption/monthly?year=${year}` : `/api/predictions/chlorine-consumption/monthly?year=${year}`;

            let monthlyConsumption = null;
            let primaryErrorMessage = '';

            const res = await fetch(url);
            if (res.ok) {
                const contentType = res.headers.get('content-type') || '';
                if (!contentType.includes('application/json')) {
                    throw new Error('Invalid response format from historical consumption service');
                }

                const data = await res.json();
                if (Array.isArray(data?.monthly_consumption)) {
                    monthlyConsumption = data.monthly_consumption;
                }
            } else {
                let backendError = '';
                try {
                    const errPayload = await res.json();
                    backendError = errPayload?.details || errPayload?.error || '';
                } catch {
                    backendError = '';
                }
                const suffix = backendError ? `: ${backendError}` : '';
                primaryErrorMessage = `Failed to fetch historical consumption (${res.status})${suffix}`;
            }

            if (!monthlyConsumption) {
                const fallbackUrl = backendUri ? `${backendUri}/data` : '/data';
                const fallbackRes = await fetch(fallbackUrl);

                if (!fallbackRes.ok) {
                    throw new Error(primaryErrorMessage || `Historical fallback data failed (${fallbackRes.status})`);
                }

                const fallbackData = await fallbackRes.json();
                const rows = Array.isArray(fallbackData)
                    ? fallbackData
                    : (Array.isArray(fallbackData?.data) ? fallbackData.data : []);

                if (!Array.isArray(rows)) {
                    throw new Error(primaryErrorMessage || 'Invalid fallback historical data format');
                }

                monthlyConsumption = buildMonthlyConsumptionFromRows(rows, year);
            }

            setHistoricalData(toMonthlyChartData(monthlyConsumption));
        } catch (err) {
            console.warn('Failed to fetch historical consumption:', err.message);
            setHistoricalError(err.message);
        } finally {
            setHistoricalLoading(false);
        }
    };

    const setStageState = (stageIndex, status, note) => {
        setPipelineStages((prev) =>
            prev.map((stage, idx) =>
                idx === stageIndex
                    ? {
                        ...stage,
                        status,
                        note
                    }
                    : stage
            )
        );
    };

    const handleRunForecast = async () => {
        if (loading) return;

        setLoading(true);
        setError(null);
        setPipelineStages(createInitialStages());
        const runStartedAt = new Date();

        const backendUri = process.env.REACT_APP_BACKEND_URI || process.env.BACKEND_URI || '';
        const url = backendUri ? `${backendUri}/api/predict` : '/api/predict';

        try {
            setStageState(0, 'running', 'Initializing model resources');
            await sleep(500);
            setStageState(0, 'done', 'Model artifacts loaded');

            setStageState(1, 'running', 'Preparing feature matrix for forecast window');
            await sleep(600);
            setStageState(1, 'done', 'Preprocessing complete');

            setStageState(2, 'running', 'Calling prediction service');
            const res = await fetch(url);
            if (!res.ok) {
                throw new Error(`Prediction request failed (${res.status})`);
            }
            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                const bodyPreview = (await res.text()).slice(0, 80);
                throw new Error(
                    `Prediction service returned a non-JSON response: ${bodyPreview}`
                );
            }

            const data = await res.json();
            const nextPredictions = data.predictions || [];
            setPredictions(nextPredictions);
            setStageState(2, 'done', `Inference returned ${nextPredictions.length} rows`);

            setStageState(3, 'running', 'Packaging forecast cards and charts');
            await sleep(400);
            setStageState(3, 'done', 'Prediction results ready for visualization');

            setLastRunMeta({
                startedAt: runStartedAt,
                completedAt: new Date(),
                rowCount: nextPredictions.length
            });
        } catch (err) {
            setError(err.message || 'Failed to load predictions. Please retry.');
            setPipelineStages((prev) => {
                const activeIndex = prev.findIndex((stage) => stage.status === 'running');
                if (activeIndex === -1) return prev;
                return prev.map((stage, idx) =>
                    idx === activeIndex
                        ? {
                            ...stage,
                            status: 'failed',
                            note: err.message || 'Unexpected failure while running pipeline'
                        }
                        : stage
                );
            });
        }

        setLoading(false);
    };

    // --- Calculate KPIs ---
    const dailyChlorine = predictions.map(p => p["Predicted_Total_Chlorine_Demand_kg/h"] * 24);
    const dailyLabels = predictions.map(p => p.Date);
    const totalDemand = dailyChlorine.reduce((a, b) => a + b, 0).toFixed(0);
    const avgDaily = (dailyChlorine.length ? (dailyChlorine.reduce((a, b) => a + b, 0) / dailyChlorine.length) : 0).toFixed(1);
    const peakDemand = dailyChlorine.length ? Math.max(...dailyChlorine).toFixed(1) : 0;

    const getForecastRange = () => {
        if (!dailyLabels.length) {
            return initialLoading ? 'Loading latest forecast...' : 'No forecast available';
        }

        const range = `${dailyLabels[0]} to ${dailyLabels[dailyLabels.length - 1]}`;
        if (lastRunMeta?.fetchedFromBackend) {
            return `${range} (Latest from Database)`;
        }
        return range;
    };

    const forecastRange = getForecastRange();
    const showChart = !loading && !initialLoading && !error && dailyChlorine.length;

    // --- Chart Data ---
    const dailyData = {
        labels: dailyLabels,
        datasets: [
            {
                label: 'Predicted Demand (kg)',
                data: dailyChlorine,
                borderColor: '#015486',
                backgroundColor: 'rgba(1, 84, 134, 0.1)',
                borderWidth: 2,
                tension: 0.4,
                fill: true,
            }
        ]
    };
    const dailyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: {
            y: { beginAtZero: false, title: { display: true, text: 'Chlorine (kg)' } },
            x: { ticks: { maxTicksLimit: 15 } }
        }
    };
    // Historical chart data
    const historicalLabels = historicalData.length > 0 
        ? historicalData.map(item => {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return monthNames[item.month - 1];
        })
        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const historicalChartData = {
        labels: historicalLabels,
        datasets: [{
            label: `Total Consumption (${selectedYear})`,
            data: historicalData.length > 0 
                ? historicalData.map(item => parseFloat(item.monthly_chlorine_consumption_kg))
                : [10800, 10750, 11160, 11160, 11532, 11664, 11880, 11736, 11376, 11304, 10800, 11067], // fallback dummy data
            backgroundColor: '#015486',
            borderRadius: 4,
        }]
    };
    const historicalOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            y: { beginAtZero: false, title: { display: true, text: 'Total Chlorine (L)' } }
        }
    };

    return (
        <div className="prediction-page">
            <div className="prediction-container">

                {/* --- Header --- */}
                <header className="page-header">
                    <div>
                        <h1 className="page-title">Chlorine Demand Forecast</h1>
                        <nav>
                            <ol className="breadcrumbs">
                                <li>
                                    <a href="/" className="breadcrumb-link">Home</a>
                                </li>
                                <ChevronRight size={16} className="breadcrumb-separator" />
                                <li>
                                    <span className="breadcrumb-active">Forecast</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <Link to="/dataentry" className="btn-action">
                        <Plus size={16} />
                        Go to Data Entry
                    </Link>
                </header>

                {/* --- Main Content Grid --- */}
                <div className="main-grid">
                    <section className="section-card col-span-1 pipeline-card">
                        <div className="pipeline-header">
                            <h2 className="section-title">ML Pipeline Status</h2>
                            <span className="pipeline-pill">
                                <Zap size={14} />
                                {loading ? 'Running' : 'Idle'}
                            </span>
                        </div>

                        <button type="button" className="btn-primary-full" onClick={handleRunForecast} disabled={loading}>
                            <BrainCircuit size={18} />
                            {loading ? 'Running Forecast...' : 'Run Forecast'}
                        </button>

                        <ul className="pipeline-steps" aria-live="polite">
                            {pipelineStages.map((stage) => (
                                <li key={stage.id} className={`pipeline-step status-${stage.status}`}>
                                    <div className="pipeline-icon" aria-hidden="true">
                                        {stage.status === 'done' && <CheckCircle2 size={18} />}
                                        {stage.status === 'running' && <LoaderCircle size={18} className="spin" />}
                                        {stage.status === 'failed' && <AlertTriangle size={18} />}
                                        {stage.status === 'idle' && <Circle size={14} />}
                                    </div>
                                    <div className="pipeline-content">
                                        <p className="pipeline-step-title">{stage.title}</p>
                                        <p className="pipeline-step-note">{stage.status === 'idle' ? stage.description : stage.note}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {lastRunMeta && (
                            <div className="pipeline-last-run">
                                <span>
                                    {lastRunMeta.fetchedFromBackend
                                        ? `Data loaded: ${lastRunMeta.completedAt.toLocaleTimeString()}`
                                        : `Last run: ${lastRunMeta.completedAt.toLocaleTimeString()}`
                                    }
                                </span>
                                <span>{lastRunMeta.rowCount} prediction rows</span>
                            </div>
                        )}
                    </section>

                    {/* Output Section */}
                    <section className="section-card col-span-2">
                        <h2 className="section-title">Chlorine Forecast: <span>{forecastRange}</span></h2>

                        {/* KPI Cards Container */}
                        <div className="kpi-container">
                            {/* Main KPI Card */}
                            <div className="kpi-main-card">
                                <div className="kpi-header">
                                    <span className="kpi-title">Predicted Total Demand</span>
                                    <Droplet size={22} />
                                </div>
                                <div className="kpi-big-value">
                                    {loading || initialLoading ? (
                                        <div className="kpi-loading">
                                            <LoaderCircle size={32} className="spin" />
                                            <span className="kpi-loading-text">
                                                {loading ? 'Calculating...' : 'Loading latest forecast...'}
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            {totalDemand} <span className="kpi-unit">kg</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Smaller KPIs */}
                            <div className="kpi-grid-small">
                                <div className="kpi-small-card">
                                    <span className="kpi-small-label">Avg. Daily Demand</span>
                                    <div className="kpi-small-value">
                                        {loading || initialLoading ? (
                                            <div className="kpi-small-loading">
                                                <LoaderCircle size={18} className="spin" />
                                            </div>
                                        ) : (
                                            <>{avgDaily} kg/day</>
                                        )}
                                    </div>
                                </div>
                                <div className="kpi-small-card">
                                    <span className="kpi-small-label">Est. Peak Demand</span>
                                    <div className="kpi-small-value">
                                        {loading || initialLoading ? (
                                            <div className="kpi-small-loading">
                                                <LoaderCircle size={18} className="spin" />
                                            </div>
                                        ) : (
                                            <>{peakDemand} kg/day</>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Line Chart */}
                        <h3 className="section-subtitle">Predicted Daily Demand (kg)</h3>
                        <div className="chart-container-sm">
                            {loading && <div className="chart-message">Forecast pipeline is running...</div>}
                            {initialLoading && <div className="chart-message">Loading latest forecast from database...</div>}
                            {error && <div className="chart-message chart-message-error">{error}</div>}
                            {!loading && !initialLoading && !error && !dailyChlorine.length && (
                                <div className="chart-message">No forecast data available.</div>
                            )}
                            {showChart ? <Line data={dailyData} options={dailyOptions} /> : null}
                        </div>
                    </section>
                </div>

                {/* --- Insights Section --- */}
                <div className="main-grid">
                    <section className="section-card col-span-1">
                        <h2 className="section-title">Key Prediction Drivers</h2>
                        <p className="text-muted">Factors influencing this forecast compared to historical averages.</p>
                        <ul className="drivers-list">
                            <li className="driver-item">
                                <Thermometer size={22} />
                                <div className="driver-info">
                                    <span className="driver-name">Avg. Temperature</span>
                                    <div className="driver-impact trend-down">
                                        <ArrowUp size={14} />
                                        <span>+1.2°C (Strong positive)</span>
                                    </div>
                                </div>
                            </li>
                            <li className="driver-item">
                                <Droplets size={22} />
                                <div className="driver-info">
                                    <span className="driver-name">Water Intake</span>
                                    <div className="driver-impact trend-down">
                                        <ArrowUp size={14} />
                                        <span>+2.5% (Moderate positive)</span>
                                    </div>
                                </div>
                            </li>
                            <li className="driver-item">
                                <CloudRain size={22} />
                                <div className="driver-info">
                                    <span className="driver-name">Total Rainfall</span>
                                    <div className="driver-impact trend-up">
                                        <ArrowDown size={14} />
                                        <span>+20mm (Slight negative)</span>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </section>

                    {/* Historical Bar Chart */}
                    <section className="section-card col-span-2">
                        <div className="historical-header">
                            <h2 className="section-title">Historical Consumption (Total L)</h2>
                            <div className="year-selector">
                                <label htmlFor="year-select" className="year-label">Year:</label>
                                <select 
                                    id="year-select"
                                    value={selectedYear} 
                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                    className="year-dropdown"
                                >
                                    <option value={2023}>2023</option>
                                    <option value={2024}>2024</option>
                                </select>
                            </div>
                        </div>
                        <div className="chart-container-lg">
                            {historicalLoading && <div className="chart-message">Loading historical data...</div>}
                            {historicalError && <div className="chart-message chart-message-error">{historicalError}</div>}
                            {!historicalLoading && !historicalError && (
                                <Bar data={historicalChartData} options={historicalOptions} />
                            )}
                        </div>
                    </section>
                </div>

            </div>
        </div>
    );
}